/**
 * useIndustrySearch Hook
 * Handles industry search and selection logic
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';
import { useValuationAuth } from './useValuationAuth';

export const useIndustrySearch = () => {
  const { getUserId, getAuthToken } = useValuationAuth();

  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(() => {
    const userId = getUserId();
    return localStorage.getItem(`advancedValuationIndustry_${userId}`) || '';
  });
  const [selectedParent, setSelectedParent] = useState(() => {
    const userId = getUserId();
    return localStorage.getItem(`advancedValuationSelectedParent_${userId}`) || '';
  });
  const [industrySearch, setIndustrySearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch industries from API
  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      if (!token) {
        logger.warn('No authentication token available');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/valuation/industries`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIndustries(response.data.industries);
    } catch (err) {
      logger.error('Error fetching industries', { error: err });
    } finally {
      setLoading(false);
    }
  };

  // Load industries on mount
  useEffect(() => {
    fetchIndustries();
  }, []);

  // Handle industry search
  const handleIndustrySearch = (searchTerm) => {
    setIndustrySearch(searchTerm);

    if (searchTerm.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    const filtered = industries.filter(industry =>
      industry.industry_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSearchResults(filtered);
    setShowSearchDropdown(filtered.length > 0);
  };

  // Select industry from search results
  const selectIndustryFromSearch = (industry) => {
    const userId = getUserId();
    const newParent = industry.industry_parent || '';
    const newIndustryId = industry.id.toString();


    setSelectedParent(newParent);
    setSelectedIndustry(newIndustryId);
    setIndustrySearch(`${industry.industry_name} (${industry.business_type === 'private' ? 'Private' : 'Public'})`);
    setShowSearchDropdown(false);

    // Save to localStorage
    localStorage.setItem(`advancedValuationSelectedParent_${userId}`, newParent);
    localStorage.setItem(`advancedValuationIndustry_${userId}`, newIndustryId);

  };

  // Get selected industry details
  const getSelectedIndustryDetails = () => {
    if (!selectedIndustry) return null;
    return industries.find(ind => ind.id.toString() === selectedIndustry);
  };

  return {
    industries,
    selectedIndustry,
    selectedParent,
    industrySearch,
    searchResults,
    showSearchDropdown,
    loading,
    setSelectedIndustry,
    setSelectedParent,
    handleIndustrySearch,
    selectIndustryFromSearch,
    getSelectedIndustryDetails,
    setShowSearchDropdown
  };
};
